<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePrivateMedia, downloadFile } from 'dashboard/composables/usePrivateMedia';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
const { t } = useI18n();
const { filteredCurrentChatAttachments, attachments } = useMessageContext();
const attachment = computed(() => attachments.value[0] || {});
const { mediaUrl, mediaError, mediaLoading, retryMedia, mediaFailed, mediaLoaded } = usePrivateMedia(() => attachment.value.dataUrl);
const showGallery = ref(false), isDownloading = ref(false);
const downloadAttachment = async () => {
  isDownloading.value = true;
  try { await downloadFile({ url: attachment.value.dataUrl, filename: attachment.value.filename, type: attachment.value.fileType, extension: attachment.value.extension }); }
  catch { useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING')); }
  finally { isDownloading.value = false; }
};
</script>
<template>
  <BaseBubble class="overflow-hidden p-3" data-bubble-name="image" @click="showGallery = true">
    <div class="relative group rounded-lg overflow-hidden" :aria-busy="mediaLoading" :style="{ minHeight: '72px', minWidth: '72px', aspectRatio: attachment.width && attachment.height ? `${attachment.width}/${attachment.height}` : undefined }">
      <div v-if="mediaError" class="flex flex-col items-center gap-2 text-center rounded-lg" role="status">
        <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
        <p class="mb-0 text-n-slate-11">{{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}</p>
        <button type="button" class="px-3 py-2 text-n-blue-11 rounded border border-n-weak" @click.stop="retryMedia">{{ $t('DEVLIVE_MEDIA.RETRY') }}</button>
      </div>
      <img v-if="mediaUrl" class="skip-context-menu" :src="mediaUrl" :width="attachment.width" :height="attachment.height" @load="mediaLoaded" @error="mediaFailed" />
      <div class="absolute right-2 bottom-2 flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-80" :aria-label="$t('DEVLIVE_MEDIA.OPEN')" />
        <Button xs solid slate icon="i-lucide-download" class="opacity-80" :aria-label="$t('DEVLIVE_MEDIA.DOWNLOAD')" :is-loading="isDownloading" :disabled="isDownloading" @click.stop="downloadAttachment" />
      </div>
    </div>
  </BaseBubble>
  <GalleryView v-if="showGallery" v-model:show="showGallery" :attachment="useSnakeCase(attachment)" :all-attachments="filteredCurrentChatAttachments" @close="showGallery = false" />
</template>
